import React, { useEffect, useState } from "react";
import AgentLayout from "../components/layout/AgentLayout";
import PageHeader from "../components/common/PageHeader";
import { DASHBOARD_CRUMB } from "../components/common/Breadcrumb";
import PageLoader from "../components/common/PageLoader";
import StatusBadge from "../components/common/StatusBadge";
import Tabs from "../components/common/Tabs";
import AgentSelect from "../components/common/AgentSelect";
import {
  changeAgentPassword,
  fetchCurrentAgent,
  logoutAgentAndRedirect,
  updateCurrentAgent,
} from "../lib/agentAuth";
import { showApiError, showSuccessAction } from "../lib/alerts";
import { formatDate, initialsFromName } from "../lib/catalog";
import { fetchCities, fetchCountries, fetchStates, findLocationLabel } from "../lib/locations";
import {
  ACCOUNT_TYPES,
  normalizeIfsc,
  parseAgentBankDetails,
  serializeAgentBankDetails,
} from "../lib/agentOnboarding";
import { formatAgencyRole, isAgencyStaffUser } from "../lib/agencyAccess";

const EMPTY_FORM = {
  full_name: "",
  designation: "",
  mobile: "",
  email: "",
  branch_name: "",
  agency_phone: "",
  address_line1: "",
  address_line2: "",
  postal_code: "",
  fax_number: "",
  business_type: "",
  office_space: "",
  billing_contact_person: "",
  billing_email: "",
  billing_mobile: "",
  billing_address: "",
  country_id: "",
  state_id: "",
  city_id: "",
  account_holder_name: "",
  account_number: "",
  ifsc_code: "",
  bank_name: "",
  bank_branch_name: "",
  account_type: "",
  payment_remarks: "",
};

function asLocationId(value) {
  const id = Number(value);
  return id > 0 ? String(id) : "";
}

function formFromProfile(profile) {
  const bank = parseAgentBankDetails(profile?.bank_details);
  return {
    ...EMPTY_FORM,
    ...Object.fromEntries(Object.keys(EMPTY_FORM).map((key) => [key, profile?.[key] || ""])),
    country_id: asLocationId(profile?.country_id),
    state_id: asLocationId(profile?.state_id),
    city_id: asLocationId(profile?.city_id),
    account_holder_name: bank.account_holder_name,
    account_number: bank.account_number,
    ifsc_code: bank.ifsc_code,
    bank_name: bank.bank_name,
    bank_branch_name: bank.branch_name,
    account_type: bank.account_type,
    payment_remarks: profile?.payment_remarks || "",
  };
}

const PROFILE_TABS = [
  { id: "profile", label: "Edit Profile" },
  { id: "password", label: "Change Password" },
];

function Field({ label, required, className = "mb-20", children }) {
  return (
    <div className={className}>
      <label className="form-label fw-semibold text-primary-light text-sm mb-8">
        {label}
        {required ? <span className="text-danger-600"> *</span> : null}
      </label>
      {children}
    </div>
  );
}

function FormSection({ title, children }) {
  return (
    <section className="agent-profile-section">
      <h6 className="agent-profile-section__title">{title}</h6>
      <div className="row g-3">{children}</div>
    </section>
  );
}

function FormCol({ span = 6, children }) {
  return <div className={`col-sm-${span}`}>{children}</div>;
}

function InfoRow({ label, value }) {
  return (
    <li>
      <span className="agent-profile-info-label text-md">{label}</span>
      <span className="agent-profile-info-value">: {value || "Not set"}</span>
    </li>
  );
}

function PasswordField({ id, label, value, onChange }) {
  const [visible, setVisible] = useState(false);
  return (
    <Field label={label} required>
      <div className="position-relative">
        <input
          id={id}
          type={visible ? "text" : "password"}
          className="form-control radius-8"
          value={value}
          onChange={onChange}
        />
        <button
          type="button"
          className="agent-password-toggle"
          aria-label={visible ? "Hide password" : "Show password"}
          onClick={() => setVisible((current) => !current)}
        >
          <iconify-icon icon={visible ? "ri:eye-off-line" : "ri:eye-line"}></iconify-icon>
        </button>
      </div>
    </Field>
  );
}

export default function ProfilePage() {
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  function setField(key, value) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  useEffect(() => {
    let alive = true;

    async function loadProfile() {
      try {
        const current = await fetchCurrentAgent();
        if (!alive) return;
        setProfile(current);
        setForm(formFromProfile(current));
      } catch (fetchError) {
        if (!alive) return;
        showApiError(fetchError, "LOAD_PROFILE");
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadProfile();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    fetchCountries().then(setCountries).catch(() => setCountries([]));
  }, []);

  useEffect(() => {
    if (!form.country_id) {
      setStates([]);
      return;
    }
    fetchStates(form.country_id).then(setStates).catch(() => setStates([]));
  }, [form.country_id]);

  useEffect(() => {
    if (!form.state_id) {
      setCities([]);
      return;
    }
    fetchCities(form.state_id).then(setCities).catch(() => setCities([]));
  }, [form.state_id]);

  const displayName = form.full_name || profile?.full_name || "Travel partner";
  const displayEmail = form.email || profile?.email || "";
  const isStaffUser = isAgencyStaffUser(profile);
  const agencyRoleLabel = formatAgencyRole(profile);
  const agencyName = profile?.agency_name || form.branch_name || profile?.branch_name || "";
  const selectedCountry = findLocationLabel(countries, form.country_id, "country_id", "country_name");
  const selectedState = findLocationLabel(states, form.state_id, "state_id", "state_name");
  const selectedCity = findLocationLabel(cities, form.city_id, "city_id", "city_name");
  const address = [form.address_line1, form.address_line2, selectedCity, selectedState, selectedCountry, form.postal_code].filter(Boolean).join(", ");

  return (
    <AgentLayout>
      <div className="container-fluid py-24">
        <PageHeader
          title={displayName}
          items={[DASHBOARD_CRUMB, { label: "Profile / Settings" }]}
          backTo="/dashboard"
          actions={
            <button
              type="button"
              className="btn btn-outline-danger btn-sm"
              onClick={async () => {
                await logoutAgentAndRedirect();
              }}
            >
              Logout
            </button>
          }
        />

        {loading ? <PageLoader message="Loading profile..." /> : null}

        {!loading && profile ? (
          <div className="row gy-4">
            <div className="col-lg-4">
              <div className="user-grid-card position-relative border radius-16 overflow-hidden bg-base h-100">
                {/* <img
                  src="/template/assets/images/user-grid/user-grid-bg1.png"
                  alt=""
                  className="agent-profile-cover w-100"
                /> */}
                <div className="pb-24 ms-16 mb-24 me-16">
                  <div className="text-center border border-top-0 border-start-0 border-end-0 pb-3">
                    {/* <span className="agent-profile-avatar">{initialsFromName(displayName)}</span> */}
                    <h6 className="mb-0 mt-16">{displayName}</h6>
                    <span className="d-block text-secondary-light mb-16">{displayEmail}</span>
                    <div className="d-flex justify-content-center gap-2 flex-wrap">
                      {isStaffUser ? (
                        <StatusBadge value={profile?.agency_role}>{agencyRoleLabel}</StatusBadge>
                      ) : (
                        <>
                          <StatusBadge value={profile?.status || "Unknown"} />
                          <StatusBadge value={profile?.verification_status || "Unknown"} />
                        </>
                      )}
                    </div>
                  </div>
                  <div className="mt-24">
                    <h6 className="text-xl mb-16">Personal Info</h6>
                    <ul className="list-unstyled mb-0 agent-profile-info-list">
                      <InfoRow label="Full Name" value={form.full_name} />
                      <InfoRow label="Email" value={form.email} />
                      <InfoRow label="Phone Number" value={form.mobile || form.agency_phone} />
                      {isStaffUser ? (
                        <>
                          <InfoRow label="Role" value={agencyRoleLabel} />
                          <InfoRow label="Agency" value={agencyName} />
                        </>
                      ) : (
                        <>
                          <InfoRow label="Branch" value={form.branch_name} />
                          <InfoRow label="Designation" value={form.designation} />
                          <InfoRow label="Business type" value={form.business_type} />
                          <InfoRow label="Country" value={selectedCountry} />
                          <InfoRow label="State" value={selectedState} />
                          <InfoRow label="City" value={selectedCity} />
                          <InfoRow label="Address" value={address} />
                        </>
                      )}
                    </ul>
                    {isStaffUser ? null : (
                    <div className="mt-24 pt-3 border-top">
                      <h6 className="text-xl mb-16">Account</h6>
                      <ul className="list-unstyled mb-0 agent-profile-info-list">
                        <InfoRow label="Registered" value={formatDate(profile?.registration_date)} />
                        <InfoRow label="Last updated" value={formatDate(profile?.last_updated)} />
                      </ul>
                    </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-8">
              <div className="card h-100">
                <div className="card-body p-24">
                  <Tabs
                    tabs={PROFILE_TABS}
                    activeId={activeTab}
                    onChange={setActiveTab}
                    className="mb-20 agent-settings-tabs"
                    ariaLabel="Profile sections"
                    idPrefix="profile-tab"
                  />

                  {activeTab === "profile" ? (
                    <div id="profile-tab-panel-profile" role="tabpanel" aria-labelledby="profile-tab-profile">
                      <form
                        onSubmit={async (event) => {
                          event.preventDefault();
                          try {
                            setSaving(true);
                            const updated = await updateCurrentAgent(
                              isStaffUser
                                ? { full_name: form.full_name, mobile: form.mobile }
                                : {
                                    ...form,
                                    bank_details: serializeAgentBankDetails({
                                      account_holder_name: form.account_holder_name,
                                      account_number: form.account_number,
                                      ifsc_code: form.ifsc_code,
                                      bank_name: form.bank_name,
                                      branch_name: form.bank_branch_name,
                                      account_type: form.account_type,
                                    }),
                                  }
                            );
                            setProfile(updated);
                            setForm(formFromProfile(updated));
                            showSuccessAction("UPDATE_PROFILE");
                          } catch (saveError) {
                            showApiError(saveError, "UPDATE_PROFILE");
                          } finally {
                            setSaving(false);
                          }
                        }}
                      >
                        <FormSection title={isStaffUser ? "Your details" : "Personal details"}>
                          <FormCol>
                            <Field label="Full Name" required className="mb-0">
                              <input className="form-control radius-8" value={form.full_name} onChange={(event) => setField("full_name", event.target.value)} />
                            </Field>
                          </FormCol>
                          {isStaffUser ? null : (
                          <FormCol>
                            <Field label="Designation" className="mb-0">
                              <input className="form-control radius-8" value={form.designation} onChange={(event) => setField("designation", event.target.value)} />
                            </Field>
                          </FormCol>
                          )}
                          <FormCol>
                            <Field label="Email" required className="mb-0">
                              <input className="form-control radius-8" value={form.email} readOnly />
                            </Field>
                          </FormCol>
                          <FormCol>
                            <Field label="Phone" className="mb-0">
                              <input className="form-control radius-8" value={form.mobile} onChange={(event) => setField("mobile", event.target.value)} />
                            </Field>
                          </FormCol>
                          {isStaffUser ? (
                            <FormCol>
                              <Field label="Role" className="mb-0">
                                <input className="form-control radius-8" value={agencyRoleLabel} readOnly />
                              </Field>
                            </FormCol>
                          ) : null}
                        </FormSection>

                        {isStaffUser ? null : (
                        <>
                        <FormSection title="Agency details">
                          <FormCol>
                            <Field label="Branch name" className="mb-0">
                              <input className="form-control radius-8" value={form.branch_name} onChange={(event) => setField("branch_name", event.target.value)} />
                            </Field>
                          </FormCol>
                          <FormCol>
                            <Field label="Agency phone" className="mb-0">
                              <input className="form-control radius-8" value={form.agency_phone} onChange={(event) => setField("agency_phone", event.target.value)} />
                            </Field>
                          </FormCol>
                          <FormCol>
                            <Field label="Business type" className="mb-0">
                              <input className="form-control radius-8" value={form.business_type} onChange={(event) => setField("business_type", event.target.value)} />
                            </Field>
                          </FormCol>
                          <FormCol>
                            <Field label="Office space" className="mb-0">
                              <input className="form-control radius-8" value={form.office_space} onChange={(event) => setField("office_space", event.target.value)} />
                            </Field>
                          </FormCol>
                          <FormCol>
                            <Field label="Fax" className="mb-0">
                              <input className="form-control radius-8" value={form.fax_number} onChange={(event) => setField("fax_number", event.target.value)} />
                            </Field>
                          </FormCol>
                        </FormSection>

                        <FormSection title="Office address">
                          <FormCol span={4}>
                            <Field label="Country" className="mb-0">
                              <AgentSelect
                                value={form.country_id}
                                options={countries.map((item) => ({ value: String(item.country_id), label: item.country_name }))}
                                onChange={(value) => setForm((current) => ({ ...current, country_id: value, state_id: "", city_id: "" }))}
                              />
                            </Field>
                          </FormCol>
                          <FormCol span={4}>
                            <Field label="State" className="mb-0">
                              <AgentSelect
                                value={form.state_id}
                                options={states.map((item) => ({ value: String(item.state_id), label: item.state_name }))}
                                onChange={(value) => setForm((current) => ({ ...current, state_id: value, city_id: "" }))}
                              />
                            </Field>
                          </FormCol>
                          <FormCol span={4}>
                            <Field label="City" className="mb-0">
                              <AgentSelect
                                value={form.city_id}
                                options={cities.map((item) => ({ value: String(item.city_id), label: item.city_name }))}
                                onChange={(value) => setField("city_id", value)}
                              />
                            </Field>
                          </FormCol>
                          <FormCol>
                            <Field label="Address line 1" className="mb-0">
                              <input className="form-control radius-8" value={form.address_line1} onChange={(event) => setField("address_line1", event.target.value)} />
                            </Field>
                          </FormCol>
                          <FormCol>
                            <Field label="Address line 2" className="mb-0">
                              <input className="form-control radius-8" value={form.address_line2} onChange={(event) => setField("address_line2", event.target.value)} />
                            </Field>
                          </FormCol>
                          <FormCol>
                            <Field label="Postal code" className="mb-0">
                              <input className="form-control radius-8" value={form.postal_code} onChange={(event) => setField("postal_code", event.target.value)} />
                            </Field>
                          </FormCol>
                        </FormSection>

                        <FormSection title="Billing">
                          <FormCol>
                            <Field label="Billing contact" className="mb-0">
                              <input className="form-control radius-8" value={form.billing_contact_person} onChange={(event) => setField("billing_contact_person", event.target.value)} />
                            </Field>
                          </FormCol>
                          <FormCol>
                            <Field label="Billing mobile" className="mb-0">
                              <input className="form-control radius-8" value={form.billing_mobile} onChange={(event) => setField("billing_mobile", event.target.value)} />
                            </Field>
                          </FormCol>
                          <FormCol>
                            <Field label="Billing email" className="mb-0">
                              <input className="form-control radius-8" value={form.billing_email} onChange={(event) => setField("billing_email", event.target.value)} />
                            </Field>
                          </FormCol>
                          <FormCol span={12}>
                            <Field label="Billing address" className="mb-0">
                              <textarea className="form-control radius-8" rows={2} value={form.billing_address} onChange={(event) => setField("billing_address", event.target.value)} />
                            </Field>
                          </FormCol>
                        </FormSection>

                        <FormSection title="Bank details">
                          <FormCol>
                            <Field label="Account holder name" className="mb-0">
                              <input className="form-control radius-8" value={form.account_holder_name} onChange={(event) => setField("account_holder_name", event.target.value)} />
                            </Field>
                          </FormCol>
                          <FormCol>
                            <Field label="Account type" className="mb-0">
                              <AgentSelect
                                value={form.account_type}
                                options={ACCOUNT_TYPES}
                                onChange={(value) => setField("account_type", value)}
                                isSearchable={false}
                                isClearable
                              />
                            </Field>
                          </FormCol>
                          <FormCol>
                            <Field label="Account number" className="mb-0">
                              <input
                                className="form-control radius-8"
                                inputMode="numeric"
                                autoComplete="off"
                                value={form.account_number}
                                onChange={(event) => setField("account_number", event.target.value.replace(/[^\d]/g, ""))}
                              />
                            </Field>
                          </FormCol>
                          <FormCol>
                            <Field label="IFSC code" className="mb-0">
                              <input
                                className="form-control radius-8"
                                autoComplete="off"
                                maxLength={11}
                                value={form.ifsc_code}
                                onChange={(event) => setField("ifsc_code", normalizeIfsc(event.target.value))}
                              />
                            </Field>
                          </FormCol>
                          <FormCol>
                            <Field label="Bank name" className="mb-0">
                              <input className="form-control radius-8" value={form.bank_name} onChange={(event) => setField("bank_name", event.target.value)} />
                            </Field>
                          </FormCol>
                          <FormCol>
                            <Field label="Branch name" className="mb-0">
                              <input className="form-control radius-8" value={form.bank_branch_name} onChange={(event) => setField("bank_branch_name", event.target.value)} />
                            </Field>
                          </FormCol>
                        </FormSection>

                        <FormSection title="Payment remarks">
                          <FormCol span={12}>
                            <Field label="Payment remarks" className="mb-0">
                              <textarea className="form-control radius-8" rows={2} value={form.payment_remarks} onChange={(event) => setField("payment_remarks", event.target.value)} />
                            </Field>
                          </FormCol>
                        </FormSection>
                        </>
                        )}

                        <div className="d-flex align-items-center justify-content-center gap-3 mt-8">
                          <button
                            type="button"
                            className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-40 py-11 radius-8"
                            onClick={() => {
                              setForm(formFromProfile(profile));
                            }}
                          >
                            Cancel
                          </button>
                          <button type="submit" className="btn btn-primary border border-primary-600 text-md px-40 py-12 radius-8" disabled={saving}>
                            {saving ? "Saving..." : "Save"}
                          </button>
                        </div>
                      </form>
                    </div>
                  ) : (
                    <div id="profile-tab-panel-password" role="tabpanel" aria-labelledby="profile-tab-password">
                      <PasswordField
                        id="current-password"
                        label="Current Password"
                        value={passwordForm.currentPassword}
                        onChange={(event) => setPasswordForm({ ...passwordForm, currentPassword: event.target.value })}
                      />
                      <PasswordField
                        id="new-password"
                        label="New Password"
                        value={passwordForm.newPassword}
                        onChange={(event) => setPasswordForm({ ...passwordForm, newPassword: event.target.value })}
                      />
                      <PasswordField
                        id="confirm-password"
                        label="Confirmed Password"
                        value={passwordForm.confirmPassword}
                        onChange={(event) => setPasswordForm({ ...passwordForm, confirmPassword: event.target.value })}
                      />
                      <div className="d-flex align-items-center justify-content-center gap-3">
                        <button
                          type="button"
                          className="btn btn-primary border border-primary-600 text-md px-40 py-12 radius-8"
                          disabled={changingPassword}
                          onClick={async () => {
                            try {
                              setChangingPassword(true);
                              await changeAgentPassword(passwordForm);
                              setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
                              showSuccessAction("UPDATE_PASSWORD");
                            } catch (passwordError) {
                              showApiError(passwordError, "UPDATE_PASSWORD");
                            } finally {
                              setChangingPassword(false);
                            }
                          }}
                        >
                          {changingPassword ? "Updating..." : "Update password"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </AgentLayout>
  );
}
